import React, { useState } from "react";
import { X, ShoppingBag, Truck, Lock, Plus, Minus, Sparkles } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { api } from "../../services/api";
import { toast } from "sonner";

export default function ProductDetailModal({ product, onClose }) {
  const { addToCart, loading: cartLoading } = useCart();
  const [activeMedia, setActiveMedia] = useState(product?.main_media_url);
  const [quantity, setQuantity] = useState(1);
  const [cep, setCep] = useState("");
  const [shippingQuote, setShippingQuote] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(false);

  if (!product) return null;

  const allMedia = [product.main_media_url, ...(product.media_urls || [])].filter(
    (url, idx, self) => url && self.indexOf(url) === idx
  );

  const handleAddToCart = () => {
    addToCart(product.product_id, quantity);
    onClose();
  };

  const handleSimulateShipping = async (e) => {
    e.preventDefault();
    const cleanCep = cep.replace(/\D/g, "");
    if (cleanCep.length !== 8) {
      toast.error("Informe um CEP válido com 8 dígitos.");
      return;
    }
    setLoadingShipping(true);
    try {
      const res = await api.post("/shipping/quote", {
        destination_cep: cleanCep,
        items: [{ product_id: product.product_id, quantity }],
      });
      setShippingQuote(res.data.options);
      toast.success("Cotação de frete Correios calculada com sucesso!");
    } catch (err) {
      toast.error("Erro ao calcular cotação de frete.");
    } finally {
      setLoadingShipping(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto animate-in fade-in">
      <div className="relative w-full max-w-4xl rounded-3xl bg-[#121214] border border-white/10 shadow-2xl p-6 sm:p-8 text-white my-8">
        
        {/* Close button */}
        <button
          onClick={onClose}
          data-testid="product-detail-close-btn"
          className="absolute top-4 right-4 z-10 p-2 rounded-xl bg-[#18181B] text-[#A1A1AA] hover:text-white border border-white/10 transition-all"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          
          {/* Gallery Column */}
          <div className="md:col-span-6 space-y-4">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[#18181B] border border-white/10">
              <img
                src={activeMedia || product.main_media_url}
                alt={product.name}
                className="w-full h-full object-cover object-center transition-all duration-300"
              />
              <div className="absolute top-3 left-3">
                <span className="px-3 py-1 rounded-full bg-[#121214]/90 text-xs font-mono font-bold text-[#FF8C69] border border-white/10 backdrop-blur-md">
                  SKU: {product.sku}
                </span>
              </div>
            </div>

            {/* Thumbnails */}
            {allMedia.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {allMedia.map((url, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveMedia(url)}
                    className={`w-16 h-20 rounded-xl overflow-hidden border-2 transition-all flex-shrink-0 ${
                      activeMedia === url ? "border-[#FF8C69]" : "border-white/10 opacity-60"
                    }`}
                  >
                    <img src={url} alt={`Preview ${i}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details Column */}
          <div className="md:col-span-6 space-y-6 text-left">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs text-[#FF8C69] font-bold mb-2">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Produto Oficial Joyce</span>
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">
                {product.name}
              </h2>
              <p className="text-xs text-[#A1A1AA] mt-2 leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Price & Availability */}
            <div className="p-4 rounded-2xl bg-[#18181B] border border-white/10 flex items-center justify-between">
              <div>
                {product.formatted_compare_at_price && (
                  <span className="block text-xs line-through text-[#A1A1AA]/60">
                    De: {product.formatted_compare_at_price}
                  </span>
                )}
                <span className="font-heading text-2xl sm:text-3xl font-bold text-[#FF8C69]">
                  {product.formatted_price}
                </span>
                <span className="block text-[11px] text-[#A1A1AA] mt-0.5">
                  Pix instantâneo ou Cartão em até 12x via InfinitePay
                </span>
              </div>

              <div className="text-right">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30">
                  {product.stock_available > 0 ? "Em Estoque" : "Esgotado"}
                </span>
                <p className="text-[11px] text-[#A1A1AA] mt-1">
                  {product.stock_available} unidades
                </p>
              </div>
            </div>

            {/* Quantity Selector & Add to Bag CTA */}
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <span className="text-xs font-bold text-[#A1A1AA]">Quantidade:</span>
                <div className="flex items-center gap-3 bg-[#18181B] border border-white/10 rounded-xl p-1.5">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    data-testid="modal-qty-decrease-btn"
                    className="p-1 text-[#A1A1AA] hover:text-white disabled:opacity-40"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-3 font-mono font-bold text-sm text-white">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(Math.min(product.stock_available || 1, quantity + 1))}
                    disabled={quantity >= (product.stock_available || 1)}
                    data-testid="modal-qty-increase-btn"
                    className="p-1 text-[#A1A1AA] hover:text-white disabled:opacity-40"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={product.stock_available === 0 || cartLoading}
                data-testid="modal-add-to-cart-btn"
                className="w-full py-4 rounded-xl bg-[#FF8C69] hover:bg-[#FFA285] text-[#0A0A0B] text-xs sm:text-sm font-bold tracking-wider uppercase shadow-[0_4px_25px_rgba(255,140,105,0.3)] transition-all flex items-center justify-center gap-2 disabled:opacity-40"
              >
                <ShoppingBag className="w-5 h-5" />
                <span>Adicionar ao Carrinho ({quantity})</span>
              </button>
            </div>

            {/* Freight Simulator */}
            <div className="p-4 rounded-2xl bg-[#18181B] border border-white/10 space-y-3">
              <span className="text-xs font-bold text-white flex items-center gap-2">
                <Truck className="w-4 h-4 text-[#FF8C69]" />
                <span>Simular Frete Correios:</span>
              </span>
              <form onSubmit={handleSimulateShipping} className="flex gap-2">
                <input
                  type="text"
                  value={cep}
                  onChange={(e) => setCep(e.target.value)}
                  placeholder="00000-000"
                  maxLength={9}
                  data-testid="modal-cep-input"
                  className="flex-1 bg-[#121214] border border-white/10 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-[#FF8C69]"
                />
                <button
                  type="submit"
                  disabled={loadingShipping}
                  data-testid="modal-cep-calc-btn"
                  className="px-4 py-2 rounded-xl bg-[#FF8C69] hover:bg-[#FFA285] text-[#0A0A0B] text-xs font-bold"
                >
                  {loadingShipping ? "Calculando..." : "Calcular"}
                </button>
              </form>
              {shippingQuote && (
                <div className="pt-2 space-y-1.5 text-xs">
                  {shippingQuote.map((opt) => (
                    <div key={opt.service_code} className="flex items-center justify-between text-[#A1A1AA]">
                      <span>{opt.service_name} ({opt.estimated_days} dias úteis):</span>
                      <strong className="text-white">{opt.formatted_price}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Discreet Packaging Guarantee */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-[#18181B] border border-white/10 text-xs text-[#A1A1AA]">
              <Lock className="w-4 h-4 text-[#FF8C69] flex-shrink-0" />
              <span>Envio 100% discreto em embalagem neutra sem logos externos.</span>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
}
